import { Checkbox, Field } from "@headlessui/react";
import { useState } from "react";
import * as z from "zod";
import validator from "validator";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldValues, useForm } from "react-hook-form";
import { classNames } from "../../../utils";
import { AirtableRecordType, RegisterFieldsType } from "../../../api/types";
import { CheckIcon, ExclamationCircleIcon } from "@heroicons/react/24/outline";

const schema = z.object({
  responsible_first_name: z
    .string()
    .nonempty("Le prénom est requis")
    .max(50, "Le prénom ne doit pas dépasser 50 caractères"),
  responsible_last_name: z
    .string()
    .nonempty("Le nom est requis")
    .max(50, "Le nom ne doit pas dépasser 50 caractères"),
  team_name: z
    .string()
    .nonempty("Le nom de l'équipe est requis")
    .max(50, "Le nom de l'équipe ne doit pas dépasser 50 caractères"),
  email: z
    .string()
    .nonempty("L'email est requis")
    .email("Adresse email invalide"),
  phone_number: z
    .string()
    .nonempty("Le numéro de téléphone est requis")
    .refine(
      validator.isMobilePhone,
      "Le numéro de téléphone doit être valide et sans espaces"
    ),
  affiliation_ffr: z
    .string()
    .nonempty("Le numéro d'affiliation FFR est requis")
    .refine(validator.isNumeric),
  automatic_registration: z.boolean().default(false),
});

export default function TeamDetailsForm({
  initialData,
  onSubmit,
  isSubmitting,
}: {
  initialData: AirtableRecordType<RegisterFieldsType> | undefined;
  onSubmit: (data: FieldValues) => void;
  isSubmitting: boolean;
}) {
  const [agreed, setAgreed] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid, isDirty },
  } = useForm({
    resolver: zodResolver(schema),
    mode: "onBlur",
    reValidateMode: "onChange",
    values: initialData?.fields,
  });

  const isValidWithPolicy = initialData ? isValid : isValid && agreed;

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full sm:mt-20">
      <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
        <div>
          <label
            htmlFor="responsible_first_name"
            className="block text-sm/6 font-semibold text-gray-900"
          >
            Prénom Responsable*
          </label>
          <div className="mt-2.5 grid grid-cols-1">
            <input
              {...register("responsible_first_name")}
              id="responsible_first_name"
              name="responsible_first_name"
              type="text"
              autoComplete="given-name"
              className={classNames(
                "col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-1.5 text-base outline outline-1 -outline-offset-1 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6",
                errors.responsible_first_name
                  ? "text-red-900 outline-red-300 placeholder:text-red-300 focus:outline-red-600"
                  : "text-gray-900 outline-gray-300 placeholder:text-gray-400 focus:outline-indigo-600"
              )}
              placeholder="Jean"
            />
            {errors.responsible_first_name && (
              <ExclamationCircleIcon
                aria-hidden="true"
                className="pointer-events-none col-start-1 row-start-1 mr-3 w-5 h-5 self-center justify-self-end text-red-500 sm:w-4 sm:h-4"
              />
            )}
          </div>
          {errors?.responsible_first_name?.message && (
            <p
              id="responsible_first_name-error"
              className="mt-2 text-sm text-red-600"
            >
              {errors.responsible_first_name.message.toString()}
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="responsible_last_name"
            className="block text-sm/6 font-semibold text-gray-900"
          >
            Nom Responsable*
          </label>
          <div className="mt-2.5 grid grid-cols-1">
            <input
              {...register("responsible_last_name")}
              id="responsible_last_name"
              name="responsible_last_name"
              type="text"
              autoComplete="family-name"
              className={classNames(
                "col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-1.5 text-base outline outline-1 -outline-offset-1 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6",
                errors.responsible_last_name
                  ? "text-red-900 outline-red-300 placeholder:text-red-300 focus:outline-red-600"
                  : "text-gray-900 outline-gray-300 placeholder:text-gray-400 focus:outline-indigo-600"
              )}
              placeholder="Dupont"
            />
            {errors.responsible_last_name && (
              <ExclamationCircleIcon
                aria-hidden="true"
                className="pointer-events-none col-start-1 row-start-1 mr-3 w-5 h-5 self-center justify-self-end text-red-500 sm:w-4 sm:h-4"
              />
            )}
          </div>
          {errors?.responsible_last_name?.message && (
            <p
              id="responsible_last_name-error"
              className="mt-2 text-sm text-red-600"
            >
              {errors.responsible_last_name.message.toString()}
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="team_name"
            className="block text-sm/6 font-semibold text-gray-900"
          >
            Nom de l'équipe*
          </label>
          <div className="mt-2.5 grid grid-cols-1">
            <input
              {...register("team_name")}
              id="team_name"
              name="team_name"
              type="text"
              autoComplete="organization"
              className={classNames(
                "col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-1.5 text-base outline outline-1 -outline-offset-1 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6",
                errors.team_name
                  ? "text-red-900 outline-red-300 placeholder:text-red-300 focus:outline-red-600"
                  : "text-gray-900 outline-gray-300 placeholder:text-gray-400 focus:outline-indigo-600"
              )}
              placeholder="Mon équipe"
            />
            {errors.team_name && (
              <ExclamationCircleIcon
                aria-hidden="true"
                className="pointer-events-none col-start-1 row-start-1 mr-3 w-5 h-5 self-center justify-self-end text-red-500 sm:w-4 sm:h-4"
              />
            )}
          </div>
          {errors?.team_name?.message && (
            <p id="team_name-error" className="mt-2 text-sm text-red-600">
              {errors.team_name.message.toString()}
            </p>
          )}
        </div>
        <div>
          <label
            htmlFor="affiliation_ffr"
            className="block text-sm/6 font-semibold text-gray-900"
          >
            N° Affiliation FFR*
          </label>
          <div className="mt-2.5 grid grid-cols-1">
            <input
              {...register("affiliation_ffr")}
              id="affiliation_ffr"
              name="affiliation_ffr"
              type="text"
              className={classNames(
                "col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-1.5 text-base outline outline-1 -outline-offset-1 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6",
                errors.affiliation_ffr
                  ? "text-red-900 outline-red-300 placeholder:text-red-300 focus:outline-red-600"
                  : "text-gray-900 outline-gray-300 placeholder:text-gray-400 focus:outline-indigo-600"
              )}
              placeholder="123456789"
            />
            {errors.affiliation_ffr && (
              <ExclamationCircleIcon
                aria-hidden="true"
                className="pointer-events-none col-start-1 row-start-1 mr-3 w-5 h-5 self-center justify-self-end text-red-500 sm:w-4 sm:h-4"
              />
            )}
          </div>
          {errors?.affiliation_ffr?.message && (
            <p id="affiliation_ffr-error" className="mt-2 text-sm text-red-600">
              {errors.affiliation_ffr.message.toString()}
            </p>
          )}
        </div>
        <div className="sm:col-span-2">
          <label
            htmlFor="email"
            className="block text-sm/6 font-semibold text-gray-900"
          >
            Email*
          </label>
          <div className="mt-2.5 grid grid-cols-1">
            <input
              {...register("email")}
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              className={classNames(
                "col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-1.5 text-base outline outline-1 -outline-offset-1 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6",
                errors.email
                  ? "text-red-900 outline-red-300 placeholder:text-red-300 focus:outline-red-600"
                  : "text-gray-900 outline-gray-300 placeholder:text-gray-400 focus:outline-indigo-600"
              )}
              placeholder="jean.dupont@gmail.com"
            />
            {errors.email && (
              <ExclamationCircleIcon
                aria-hidden="true"
                className="pointer-events-none col-start-1 row-start-1 mr-3 w-5 h-5 self-center justify-self-end text-red-500 sm:w-4 sm:h-4"
              />
            )}
          </div>
          {errors?.email?.message && (
            <p id="email-error" className="mt-2 text-sm text-red-600">
              {errors.email.message.toString()}
            </p>
          )}
        </div>
        <div className="sm:col-span-2">
          <label
            htmlFor="phone_number"
            className="block text-sm/6 font-semibold text-gray-900"
          >
            Numéro de téléphone*
          </label>
          <div className="mt-2.5 grid grid-cols-1">
            <input
              {...register("phone_number")}
              id="phone_number"
              name="phone_number"
              type="tel"
              autoComplete="tel"
              className={classNames(
                "col-start-1 row-start-1 block w-full rounded-md bg-white px-3 py-1.5 text-base outline outline-1 -outline-offset-1 focus:outline focus:outline-2 focus:-outline-offset-2 sm:text-sm/6",
                errors.phone_number
                  ? "text-red-900 outline-red-300 placeholder:text-red-300 focus:outline-red-600"
                  : "text-gray-900 outline-gray-300 placeholder:text-gray-400 focus:outline-indigo-600"
              )}
              placeholder="+33 6 12 34 56 78"
            />
            {errors.phone_number && (
              <ExclamationCircleIcon
                aria-hidden="true"
                className="pointer-events-none col-start-1 row-start-1 mr-3 w-5 h-5 self-center justify-self-end text-red-500 sm:w-4 sm:h-4"
              />
            )}
          </div>
          {errors?.phone_number?.message && (
            <p id="phone_number-error" className="mt-2 text-sm text-red-600">
              {errors.phone_number.message.toString()}
            </p>
          )}
        </div>
        {!initialData && (
          <>
            {/* <Field className="flex gap-x-4 sm:col-span-2">
              <div className="flex h-6 items-center">
                <Controller
                  render={({ field: { value, onChange, ...rest } }) => (
                    <Checkbox
                      {...rest}
                      checked={value}
                      onChange={onChange}
                      className={classNames(
                        "group w-6 h-6 rounded-md p-1",
                        value ? "bg-indigo-600" : "bg-gray-200"
                      )}
                    >
                      {value ? (
                        <CheckIcon className="w-4 h-4 text-white" />
                      ) : null}
                    </Checkbox>
                  )}
                  control={control}
                  name="automatic_registration"
                />
              </div>
              <div className="text-sm/6 text-gray-600">
                Cochez si vous souhaitez automatiquement{" "}
                <a
                  href="https://api.www.ffr.fr/wp-content/uploads/2025/01/inscription-circuit-sevens-accession-2025.pdf"
                  className="font-semibold text-indigo-600"
                >
                  inscrire votre équipe sur le Circuit
                </a>{" "}
                auprès de la FFR. Ce document sera automatiquement généré et
                envoyé à la FFR.
              </div>
            </Field> */}
            <Field className="flex gap-x-4 sm:col-span-2">
              <div className="flex h-6 items-center">
                <Checkbox
                  checked={agreed}
                  onChange={setAgreed}
                  className={classNames(
                    "group w-6 h-6 rounded-md p-1",
                    agreed ? "bg-indigo-600" : "bg-gray-200"
                  )}
                >
                  {agreed ? <CheckIcon className="w-4 h-4 text-white" /> : null}
                </Checkbox>
              </div>
              <div className="flex text-sm/6 text-gray-600">
                J'affirme avoir lu et j'accèpte&nbsp;
                <a
                  href="https://api.www.ffr.fr/wp-content/uploads/2024/10/reglement-sportif-rugby-a-7.pdf"
                  target="_blank"
                  className="font-semibold text-indigo-600"
                  rel="noreferrer"
                >
                  le règlement sportif du rugby à 7
                </a>
                .
              </div>
            </Field>
          </>
        )}
      </div>
      <div className="mt-10">
        {!initialData && (
          <button
            type="submit"
            disabled={!isValidWithPolicy || isSubmitting}
            className={classNames(
              "block w-full rounded-md bg-indigo-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
              isSubmitting || !isValidWithPolicy
                ? "cursor-not-allowed opacity-50 disabled"
                : ""
            )}
          >
            {!isSubmitting ? "Inscrire mon équipe" : "Enregistrement ..."}
            {isSubmitting && (
              <svg
                aria-hidden="true"
                role="status"
                className="inline mx-2 w-4 h-4 text-white animate-spin"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="#E5E7EB"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>
        )}
        {initialData && (
          <button
            type="submit"
            disabled={!isDirty || isSubmitting}
            className={classNames(
              "block w-full rounded-md bg-indigo-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600",
              !isDirty || isSubmitting ? "cursor-not-allowed opacity-50" : ""
            )}
          >
            {!isSubmitting ? "Mettre à jour mon équipe" : "Enregistrement ..."}
            {isSubmitting && (
              <svg
                aria-hidden="true"
                role="status"
                className="inline mx-2 w-4 h-4 text-white animate-spin"
                viewBox="0 0 100 101"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                  fill="#E5E7EB"
                />
                <path
                  d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>
        )}
      </div>
    </form>
  );
}
